import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, BadgeCheck, ClipboardCheck, Handshake, FileCheck, ChevronRight } from "lucide-react";
import { COMPANY, SERVICES, INDUSTRIES, IMAGES, FEATURED_CLIENTS, CLIENTS } from "@/data/site";
import { Reveal, SectionHeading, Eyebrow, CTABand, MaskedLine, ICONS } from "@/components/Common";

const MARQUEE_ITEMS = ["Manpower Outsourcing", "Security Services", "Facility Management", "Sanitation & Cleaning", "Warehousing Management", "Event Security", "Specialized Workforce Solutions"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0.2]);

  return (
    <section ref={ref} className="relative min-h-[94vh] flex flex-col bg-navy overflow-hidden hero-grain" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Professional corporate workforce" className="w-full h-[120%] object-cover object-center opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative flex-1 flex items-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-20 pb-16">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center gap-3 text-xs font-semibold tracking-[0.24em] uppercase text-gold mb-7 border border-gold/30 px-4 py-2"
            data-testid="hero-eyebrow"
          >
            Incorporated 07 February 2003 · Chandigarh, India
          </motion.p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-[4.2rem] font-extrabold tracking-tight text-white leading-[1.04]" data-testid="hero-headline">
            <MaskedLine delay={0.25}>Reliable Manpower.</MaskedLine>
            <MaskedLine delay={0.4}>Professional Services.</MaskedLine>
            <MaskedLine delay={0.55} className="text-gold">Trusted Since 2003.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
            data-testid="hero-subtext"
          >
            {COMPANY.name} provides manpower outsourcing, security manpower, facility management, sanitation, warehousing and specialized workforce solutions for organizations requiring dependable and professionally managed personnel.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/request-quote"
              data-testid="hero-quote-btn"
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy font-heading font-bold text-xs tracking-widest uppercase px-8 py-4 transition-all hover:gap-3.5"
            >
              Request a Quote <ArrowRight size={15} />
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold text-white hover:text-gold font-heading font-bold text-xs tracking-widest uppercase px-8 py-4 transition-colors"
            >
              <Phone size={15} /> Talk to Our Team
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.25 }}
        className="relative border-t border-white/10"
        data-testid="hero-trust-strip"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
          {["23+ Years of Experience", "Diverse Workforce Solutions", "Professional Service Management"].map((item) => (
            <div key={item} className="py-5 sm:py-6 sm:px-8 first:pl-0 flex items-center gap-3">
              <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" />
              <span className="text-sm font-medium text-slate-200 tracking-wide">{item}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

const Marquee = () => (
  <div className="bg-gold-soft border-y border-gold/20 py-4 overflow-hidden" data-testid="services-marquee" aria-hidden="true">
    <div className="animate-marquee flex whitespace-nowrap w-max">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
        <span key={i} className="inline-flex items-center gap-6 mx-6 text-navy font-heading font-semibold text-sm tracking-[0.18em] uppercase">
          {item} <span className="w-1.5 h-1.5 bg-gold rotate-45 inline-block" />
        </span>
      ))}
    </div>
  </div>
);

const Intro = () => (
  <section className="py-16 sm:py-20 lg:py-28 bg-white" data-testid="intro-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <Reveal className="relative">
        <div className="absolute -top-5 -left-5 w-full h-full border-2 border-gold pointer-events-none" />
        <img src={IMAGES.industrial} alt="Daksh workforce at an industrial facility" className="relative w-full h-[420px] sm:h-[500px] object-cover" />
        <div className="absolute -bottom-7 -right-4 sm:right-8 bg-navy text-white px-7 py-5 shadow-2xl">
          <p className="font-heading text-4xl font-extrabold text-gold leading-none">23+</p>
          <p className="text-xs tracking-[0.2em] uppercase text-slate-300 mt-2">Years of Operations</p>
        </div>
      </Reveal>
      <div>
        <Reveal>
          <Eyebrow>About Daksh</Eyebrow>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy mt-4 leading-tight">
            A Trusted Workforce Partner Since 2003
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-slate-600 leading-relaxed">
            {COMPANY.name} is a Non-Government Private Limited Company incorporated on {COMPANY.incorporated} and engaged primarily in the Business Services sector.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            With more than two decades of experience, Daksh provides manpower outsourcing, security manpower, facility management, sanitation, warehousing management, event security and specialized workforce solutions.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Our objective is to provide organizations with dependable manpower and professionally managed workforce solutions based on their operational requirements.
          </p>
        </Reveal>
        <Reveal delay={0.2} className="mt-8">
          <Link
            to="/about"
            data-testid="intro-about-btn"
            className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white font-heading font-bold text-xs tracking-widest uppercase px-7 py-4 transition-all hover:gap-3"
          >
            About Daksh <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesGrid = () => (
  <section className="py-16 sm:py-20 lg:py-24 bg-[#FAFAFC]" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        center
        eyebrow="What We Do"
        title="Workforce Solutions Across Every Operational Need"
        desc="Six focused service verticals, one accountable partner. Every deployment is supervised, structured and built around your requirements."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          return (
            <Reveal key={s.slug} delay={(i % 3) * 0.1}>
              <Link
                to={s.path}
                data-testid={`service-card-${s.slug}`}
                className="group block bg-white border border-slate-200 p-8 h-full transition-all duration-300 hover:border-gold hover:shadow-xl hover:shadow-navy/5 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-navy flex items-center justify-center mb-6 transition-colors group-hover:bg-gold">
                  <Icon size={22} className="text-gold transition-colors group-hover:text-navy" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-navy tracking-snug">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.short}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-gold-hover">
                  Learn More <ChevronRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const WHY = [
  { icon: BadgeCheck, title: "Two Decades of Operations", desc: "Incorporated in February 2003, Daksh brings over twenty-three years of continuous, on-ground operational experience." },
  { icon: ClipboardCheck, title: "Supervised Deployment", desc: "Every site is backed by supervisory personnel, attendance monitoring and structured reporting — not just manpower supply." },
  { icon: FileCheck, title: "Compliance-Focused", desc: "Wages, attendance and statutory obligations for deployed personnel are managed with a compliance-first approach." },
  { icon: Handshake, title: "Single Accountable Partner", desc: "One point of contact for recruitment, deployment, supervision and replacement across all service verticals." },
];

const WhyDaksh = () => (
  <section className="py-16 sm:py-20 lg:py-24 bg-navy relative overflow-hidden hero-grain" data-testid="why-daksh-section">
    <div className="absolute -left-24 top-0 w-96 h-96 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <SectionHeading
        light
        eyebrow="Why Daksh"
        title="Professionalism Built Over Two Decades"
        desc="We communicate through structure, supervision and reliability — not exaggerated claims."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
        {WHY.map((item, i) => (
          <Reveal key={item.title} delay={i * 0.08} className="bg-navy">
            <div className="p-8 h-full border border-transparent transition-colors hover:border-gold/40" data-testid={`why-item-${i + 1}`}>
              <span className="font-heading text-outline-gold text-5xl font-extrabold">0{i + 1}</span>
              <item.icon size={22} className="text-gold mt-6" />
              <h3 className="font-heading text-lg font-semibold text-white mt-4">{item.title}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{item.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const IndustriesTeaser = () => (
  <section className="py-16 sm:py-20 lg:py-24 bg-white" data-testid="industries-teaser">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        center
        eyebrow="Who We Serve"
        title="Industries & Establishments We Support"
        desc="From government institutions to commercial establishments — wherever dependable manpower is required."
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
        {INDUSTRIES.slice(0, 8).map((ind, i) => {
          const Icon = ICONS[ind.icon];
          return (
            <Reveal key={ind.name} delay={(i % 4) * 0.07}>
              <div className="border border-slate-200 bg-white p-6 text-center h-full transition-all hover:border-gold hover:shadow-lg hover:shadow-navy/5 hover:-translate-y-0.5">
                <Icon size={26} className="text-gold-hover mx-auto" />
                <p className="mt-4 text-sm font-semibold text-navy">{ind.name}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal className="text-center mt-10">
        <Link to="/industries" data-testid="industries-view-all-btn" className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-navy border-b-2 border-gold pb-1 hover:text-gold-hover transition-colors">
          View All Industries <ArrowRight size={14} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ClientsSection = () => (
  <section className="py-16 sm:py-20 lg:py-24 bg-white" data-testid="clients-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        center
        eyebrow="Our Clients"
        title="Trusted by Government & Public Institutions"
        desc="A selection of organizations that have relied on Daksh for manpower, security and facility services."
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
        {FEATURED_CLIENTS.map((c, i) => (
          <Reveal key={c.name} delay={i * 0.08}>
            <div className="relative bg-navy text-white p-8 border-t-4 border-gold h-full hero-grain overflow-hidden" data-testid={`client-featured-${i + 1}`}>
              <span className="font-heading text-outline-gold text-5xl font-extrabold">0{i + 1}</span>
              <p className="font-heading font-bold text-xl mt-4 leading-snug">{c.name}</p>
              <p className="text-xs text-slate-300 mt-2 tracking-[0.18em] uppercase">{c.place}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
        {CLIENTS.map((c, i) => (
          <Reveal key={`${c.name}-${i}`} delay={(i % 3) * 0.04}>
            <div className="flex items-baseline gap-3 py-3.5 border-b border-slate-200" data-testid={`client-item-${i + 1}`}>
              <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" />
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="font-semibold text-navy">{c.name}</span>
                {c.place ? <span className="text-slate-500"> — {c.place}</span> : null}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <Intro />
    <ServicesGrid />
    <WhyDaksh />
    <ClientsSection />
    <IndustriesTeaser />
    <CTABand />
  </>
);

export default Home;
